import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  Menu,
  MenuItem,
  Divider,
  Collapse
} from '@mui/material';
import { Plus, Pencil, DotsThreeVertical, PaperPlaneTilt, Eye, SquaresFour, Archive, ArrowLeft, SignatureIcon, PlusCircle, CaretDown, CaretRight } from '@phosphor-icons/react';
import CreateNewProposalDialog from './CreateNewProposalDialog';
import { format } from 'date-fns';
import api from '../../../utils/api';
import { useBusiness } from '../../../context/BusinessContext';

const STATUS_COLORS = {
  draft: 'default',
  sent: 'info',
  accepted: 'success',
  declined: 'error',
  expired: 'default',
  archived: 'default',
  scheduled: 'warning'
};

const isOriginalAgreement = (agreement) => !agreement.parentAgreement && agreement.agreementType !== 'addendum';

function ProposalList() {
  const navigate = useNavigate();
  const { selectedBusiness, loading: businessLoading } = useBusiness();
  const [proposals, setProposals] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [expandedThreads, setExpandedThreads] = useState({});

  const fetchProposals = useCallback(async () => {
    try {
      setLoading(true);
      const businessId = selectedBusiness?.business?._id;
      if (!businessId) {
        setLoading(false);
        return;
      }
      const response = await api.get(`/api/agreements/business/${businessId}`, {
        params: showArchived ? { status: 'archived' } : {}
      });
      setProposals(response.data || []);
    } catch (err) {
      setError('Failed to load agreements');
    } finally {
      setLoading(false);
    }
  }, [selectedBusiness?.business?._id, showArchived]);

  useEffect(() => {
    if (businessLoading) return;
    const businessId = selectedBusiness?.business?._id;
    if (businessId) fetchProposals();
  }, [businessLoading, selectedBusiness?.business?._id, fetchProposals]);

  const handleCreateDialogClose = (templateId) => {
    setCreateDialogOpen(false);
    if (templateId === undefined) return;
    const path = templateId
      ? `/supplier/agreements/new?templateId=${templateId}`
      : '/supplier/agreements/new';
    navigate(path);
  };

  const handleMenuOpen = (event, proposal) => {
    setAnchorEl(event.currentTarget);
    setSelectedProposal(proposal);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedProposal(null);
  };

  const handleSend = async () => {
    if (!selectedProposal) return;
    try {
      const res = await api.post(`/api/agreements/${selectedProposal._id}/send`);
      fetchProposals();
      handleMenuClose();
      if (res.data?.agreementUrl) {
        navigator.clipboard?.writeText(res.data.agreementUrl);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send agreement');
    }
  };

  const handleArchive = async () => {
    if (!selectedProposal) return;
    try {
      await api.post(`/api/agreements/${selectedProposal._id}/archive`);
      fetchProposals();
      handleMenuClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to archive agreement');
    }
  };

  const handleCreateAddendum = async () => {
    if (!selectedProposal) return;
    try {
      const { data: addendum } = await api.post(`/api/agreements/${selectedProposal._id}/addendum`);
      handleMenuClose();
      if (addendum?._id) {
        navigate(`/supplier/agreements/${addendum._id}/edit`);
      } else {
        await fetchProposals();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create addendum');
    }
  };

  const groupedProposals = React.useMemo(() => {
    const originals = proposals.filter(isOriginalAgreement);
    const childrenByParent = proposals.reduce((acc, item) => {
      if (!item.parentAgreement) return acc;
      const parentId = typeof item.parentAgreement === 'object' ? item.parentAgreement?._id : item.parentAgreement;
      if (!parentId) return acc;
      acc[parentId] = acc[parentId] || [];
      acc[parentId].push(item);
      return acc;
    }, {});

    return originals.map((original) => ({
      original,
      addendums: (childrenByParent[original._id] || []).sort((a, b) => (a.versionNumber || 1) - (b.versionNumber || 1))
    }));
  }, [proposals]);

  const businessId = selectedBusiness?.business?._id;
  const clientUrl = process.env.REACT_APP_CLIENT_URL || window.location.origin;

  if (businessLoading || !businessId) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          {showArchived && (
            <IconButton
              onClick={() => setShowArchived(false)}
              aria-label="Back"
            >
              <ArrowLeft size={20} />
            </IconButton>
          )}
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {showArchived ? 'Archived agreements' : 'Agreements'}
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            onClick={() => navigate('/supplier/products')}
          >
            Products and Services
          </Button>
          <Button
            variant="contained"
            startIcon={<SquaresFour size={20} />}
            onClick={() => navigate('/supplier/agreements/templates')}
            sx={{
              bgcolor: '#000',
              color: '#fff',
              '&:hover': { bgcolor: '#111' }
            }}
          >
            Templates
          </Button>
        </Box>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        Create, send, and track agreements so couples get clear pricing and next steps.
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ overflow: 'hidden' }}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Couple</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Sent</TableCell>
                  <TableCell align="right" width={60} />
                </TableRow>
              </TableHead>
              <TableBody>
                {!showArchived && (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ py: 1.5 }}>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<Plus size={18} />}
                        onClick={() => setCreateDialogOpen(true)}
                        sx={{
                          py: 1.25,
                          justifyContent: 'flex-start',
                          textTransform: 'none',
                          borderStyle: 'dashed',
                          borderColor: 'divider',
                          color: 'text.primary',
                          '& .MuiButton-startIcon': { mr: 1 },
                          '&:hover': {
                            borderColor: '#f06292',
                            bgcolor: '#fce4ec'
                          }
                        }}
                      >
                        Create a new agreement
                      </Button>
                    </TableCell>
                  </TableRow>
                )}
                {groupedProposals.map(({ original, addendums }) => {
                  const isExpanded = !!expandedThreads[original._id];
                  return (
                    <React.Fragment key={original._id}>
                      <TableRow hover>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            {addendums.length > 0 && (
                              <IconButton
                                size="small"
                                onClick={() =>
                                  setExpandedThreads((prev) => ({ ...prev, [original._id]: !prev[original._id] }))
                                }
                                aria-label={isExpanded ? 'Collapse addendums' : 'Expand addendums'}
                              >
                                {isExpanded ? <CaretDown size={16} /> : <CaretRight size={16} />}
                              </IconButton>
                            )}
                            <Box>
                              <Typography variant="body2" fontWeight={500}>
                                {original.lead?.name || 'Not set'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Original agreement
                              </Typography>
                              {original.lead?.email && (
                                <Typography variant="caption" color="text.secondary">
                                  {original.lead.email}
                                </Typography>
                              )}
                              {original.updatedAfterSendAt && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Updated {format(new Date(original.updatedAfterSendAt), 'dd MMM yyyy HH:mm')}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={original.status}
                            size="small"
                            color={STATUS_COLORS[original.status] || 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          {original.sentAt ? format(new Date(original.sentAt), 'dd MMM yyyy') : '—'}
                          {addendums.length > 0 && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {addendums.length} addendum{addendums.length > 1 ? 's' : ''}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, original)}
                            aria-label="Actions"
                          >
                            <DotsThreeVertical size={18} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                      {addendums.length > 0 && (
                        <TableRow>
                          <TableCell colSpan={4} sx={{ py: 0, borderBottom: 'none' }}>
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Box sx={{ pl: 5, py: 1 }}>
                                {addendums.map((addendum) => (
                                  <Box
                                    key={addendum._id}
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      py: 0.75,
                                      borderBottom: '1px solid',
                                      borderColor: 'divider'
                                    }}
                                  >
                                    <Box>
                                      <Typography variant="body2" fontWeight={500}>
                                        Addendum v{addendum.versionNumber || '2'}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" display="block">
                                        Linked to original agreement
                                      </Typography>
                                      {addendum.updatedAfterSendAt && (
                                        <Typography variant="caption" color="text.secondary" display="block">
                                          Updated {format(new Date(addendum.updatedAfterSendAt), 'dd MMM yyyy HH:mm')}
                                        </Typography>
                                      )}
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Chip
                                        label={addendum.status}
                                        size="small"
                                        color={STATUS_COLORS[addendum.status] || 'default'}
                                      />
                                      <IconButton
                                        size="small"
                                        onClick={(e) => handleMenuOpen(e, addendum)}
                                        aria-label="Addendum actions"
                                      >
                                        <DotsThreeVertical size={18} />
                                      </IconButton>
                                    </Box>
                                  </Box>
                                ))}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
                {groupedProposals.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ py: 4 }}>
                      <Box textAlign="center">
                        <SignatureIcon size={40} weight="light" style={{ opacity: 0.5, marginBottom: 10 }} />
                        <Typography variant="body2" color="text.secondary">
                          No agreements yet
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          startIcon={<Archive size={18} />}
          onClick={() => setShowArchived((prev) => !prev)}
        >
          {showArchived ? 'View active' : 'Archive'}
        </Button>
      </Box>

      <CreateNewProposalDialog
        open={createDialogOpen}
        onClose={handleCreateDialogClose}
        businessId={selectedBusiness?.business?._id}
      />

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem
          onClick={() => {
            navigate(`/supplier/agreements/${selectedProposal?._id}`);
            handleMenuClose();
          }}
        >
          <Eye size={18} style={{ marginRight: 8 }} />
          View
        </MenuItem>
        {['draft', 'sent', 'scheduled'].includes(selectedProposal?.status) && (
          <>
            <MenuItem
              onClick={() => {
                navigate(`/supplier/agreements/${selectedProposal?._id}/edit`);
                handleMenuClose();
              }}
            >
              <Pencil size={18} style={{ marginRight: 8 }} />
              Edit
            </MenuItem>
          </>
        )}
        {['draft', 'scheduled'].includes(selectedProposal?.status) && (
          <>
            <MenuItem onClick={handleSend}>
              <PaperPlaneTilt size={18} style={{ marginRight: 8 }} />
              Send
            </MenuItem>
          </>
        )}
        {selectedProposal?.status === 'accepted' && isOriginalAgreement(selectedProposal) && (
          <MenuItem onClick={handleCreateAddendum}>
            <PlusCircle size={18} style={{ marginRight: 8 }} />
            Create addendum
          </MenuItem>
        )}
        {selectedProposal?.status === 'sent' && selectedProposal?.publicToken && (
          <MenuItem
            onClick={() => {
              const url = `${clientUrl}/agreement/${selectedProposal.publicToken}`;
              navigator.clipboard?.writeText(url);
              handleMenuClose();
            }}
          >
            Copy link
          </MenuItem>
        )}
        {selectedProposal?.status !== 'archived' && (
          <>
            <Divider />
            <MenuItem onClick={handleArchive}>
              <Archive size={18} style={{ marginRight: 8 }} />
              Archive
            </MenuItem>
          </>
        )}
      </Menu>
    </Box>
  );
}

export default ProposalList;
